import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import './AdminCategoryRequests.css';

function AdminCategoryRequests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [sortBy, setSortBy] = useState('date');
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [icon, setIcon] = useState('🏷️');
  const [rejectionReason, setRejectionReason] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
  }, [filter, sortBy, page]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await apiFetch(
        `/api/category-requests?status=${filter}&sortBy=${sortBy}&page=${page}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests);
        setTotalPages(data.totalPages);
      } else if (response.status === 401) {
        navigate('/login');
      } else if (response.status === 403) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setEditedName(request.category_name);
    setEditedDescription(request.description);
    setIcon('🏷️');
    setShowApproveModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const submitApproval = async () => {
    try {
      const response = await apiFetch(
        `/api/category-requests/${selectedRequest.request_id}/approve`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({
            editedName: editedName !== selectedRequest.category_name ? editedName : null,
            editedDescription: editedDescription !== selectedRequest.description ? editedDescription : null,
            icon
          })
        }
      );

      if (response.ok) {
        setShowApproveModal(false);
        setSelectedRequest(null);
        fetchRequests();
      }
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const response = await apiFetch(
        `/api/category-requests/${selectedRequest.request_id}/reject`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({ reason: rejectionReason })
        }
      );

      if (response.ok) {
        setShowRejectModal(false);
        setSelectedRequest(null);
        fetchRequests();
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { className: 'status-pending', text: 'Pending Review' },
      approved: { className: 'status-approved', text: 'Approved' },
      rejected: { className: 'status-rejected', text: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.className}`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="admin-category-requests-page">
      <div className="page-header">
        <div>
          <h1>Category Request Management</h1>
          <p className="page-subtitle">
            Review and manage identity category requests from users
          </p>
        </div>
        <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>

      <div className="controls-row">
        <div className="filter-tabs">
          <button
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => { setFilter('pending'); setPage(1); }}
          >
            Pending
          </button>
          <button
            className={filter === 'approved' ? 'active' : ''}
            onClick={() => { setFilter('approved'); setPage(1); }}
          >
            Approved
          </button>
          <button
            className={filter === 'rejected' ? 'active' : ''}
            onClick={() => { setFilter('rejected'); setPage(1); }}
          >
            Rejected
          </button>
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => { setFilter('all'); setPage(1); }}
          >
            All
          </button>
        </div>

        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
            <option value="date">Date</option>
            <option value="upvotes">Upvotes</option>
            <option value="trust_score">Trust Score</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No requests found</h3>
          <p>
            {filter === 'pending'
              ? "There are no pending category requests at this time."
              : `No ${filter} requests found.`}
          </p>
        </div>
      ) : (
        <>
          <div className="requests-list">
            {requests.map((request) => (
              <div key={request.request_id} className="request-card">
                <div className="request-header">
                  <div>
                    <h3>{request.category_name}</h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="requester-info">
                    <span className="requester-name">
                      {request.requester_first_name}
                    </span>
                    <span className="trust-score">
                      Trust: {request.requester_trust_score}
                    </span>
                    <span className="account-age">
                      Account: {Math.floor(request.account_age_days)} days
                    </span>
                  </div>
                </div>

                <p className="request-description">{request.description}</p>

                <div className="request-reason">
                  <strong>Reason:</strong> {request.reason}
                </div>

                <div className="request-meta">
                  <span className="meta-item">
                    📅 {formatDate(request.created_at)}
                  </span>
                  {request.upvote_count > 0 && (
                    <span className="meta-item">
                      👍 {request.upvote_count} {request.upvote_count === 1 ? 'upvote' : 'upvotes'}
                    </span>
                  )}
                </div>

                {request.status === 'pending' && (
                  <div className="action-buttons">
                    <button
                      className="btn-approve"
                      onClick={() => handleApprove(request)}
                    >
                      ✓ Approve
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleReject(request)}
                    >
                      ✗ Reject
                    </button>
                  </div>
                )}

                {request.status === 'approved' && (
                  <div className="review-info">
                    ✅ Approved by {request.reviewer_first_name} on {formatDate(request.reviewed_at)}
                  </div>
                )}

                {request.status === 'rejected' && (
                  <div className="review-info rejection">
                    <strong>Rejected by {request.reviewer_first_name}:</strong>
                    <p>{request.rejection_reason}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showApproveModal && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Approve Category Request</h2>
            <p className="modal-subtitle">
              Review and edit the category details before approval
            </p>

            <div className="form-group">
              <label>Category Name</label>
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                maxLength={200}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Icon (emoji)</label>
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                maxLength={2}
                placeholder="🏷️"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowApproveModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={submitApproval}>
                Approve Category
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reject Category Request</h2>
            <p className="modal-subtitle">
              Please provide a reason for rejection
            </p>

            <div className="form-group">
              <label>Rejection Reason</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Explain why this category request cannot be approved..."
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={submitRejection}>
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCategoryRequests;
